/** How long the login check may take before the portal offers a way out of its spinner. */
export const AUTH_STUCK_AFTER_MS = 8000;

/** Longest the reset waits on any one browser API; a hung IndexedDB (the very thing being recovered from) never answers. */
const RESET_STEP_TIMEOUT_MS = 2000;
const PROBE_TIMEOUT_MS = 5000;

const withTimeout = <T>(work: Promise<T>, fallback: T): Promise<T> =>
  Promise.race([work, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), RESET_STEP_TIMEOUT_MS))]);

/**
 * Clears what a stuck browser profile keeps for this site (Firebase's login database, local and session storage and
 * the Cache API), the same thing "Empty cache and hard reload" fixes by hand. The person has to sign in again.
 */
export const resetBrowserState = async (): Promise<void> => {
  try {
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch {
    // storage can be blocked; carry on with the rest
  }
  try {
    if ("caches" in window) await withTimeout(
        caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key)))),
        []
      );
  } catch {
    // ignore
  }
  try {
    // Safari can leave databases() pending forever on a stuck profile, so fall back to the one database that matters
    const fallback = [{ name: "firebaseLocalStorageDb" }];
    const databases = indexedDB.databases ? await withTimeout(indexedDB.databases(), fallback) : fallback;
    await Promise.all(
      databases.map((db) =>
        withTimeout(
          new Promise<void>((resolve) => {
            if (!db.name) return resolve();
            const request = indexedDB.deleteDatabase(db.name);
            request.onsuccess = request.onerror = request.onblocked = () => resolve(); // a blocked delete finishes once other tabs close
          }),
          undefined
        )
      )
    );
  } catch {
    // ignore
  }
};

const probe = async (work: () => Promise<unknown>): Promise<string> => {
  const started = Date.now();
  try {
    const result = await Promise.race([
      work().then(() => "ok"),
      new Promise<string>((resolve) => setTimeout(() => resolve("NO ANSWER after " + PROBE_TIMEOUT_MS + "ms"), PROBE_TIMEOUT_MS)),
    ]);
    return `${result} (${Date.now() - started}ms)`;
  } catch (error: any) {
    return `error: ${error?.name ?? ""} ${error?.message ?? error}`;
  }
};

/** Facts about what this browser's storage does, shown on the stuck-login screen and logged, so a hang can be pinned on a specific API. */
export const diagnoseBrowserState = async (): Promise<string[]> => {
  const lines = [
    `build: ${process.env.REACT_APP_BUILD_TIME ?? "unknown"}`,
    `browser: ${navigator.userAgent}`,
    `online: ${navigator.onLine}`,
    `localStorage: ${await probe(async () => { window.localStorage.setItem("_probe", "1"); window.localStorage.removeItem("_probe"); })}`,
    `indexedDB.open: ${await probe(() => new Promise((resolve, reject) => {
      const request = indexedDB.open("_probe");
      request.onsuccess = () => { request.result.close(); indexedDB.deleteDatabase("_probe"); resolve(true); };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("blocked"));
    }))}`,
    `indexedDB.databases: ${await probe(async () => (await indexedDB.databases()).map((db) => db.name).join(","))}`,
    `firebaseLocalStorageDb.open: ${await probe(() => new Promise((resolve, reject) => {
      const request = indexedDB.open("firebaseLocalStorageDb");
      request.onsuccess = () => { request.result.close(); resolve(true); };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("blocked"));
    }))}`,
    `identitytoolkit.googleapis.com: ${await probe(() => fetch("https://identitytoolkit.googleapis.com/", { mode: "no-cors", cache: "no-store" }))}`,
    `securetoken.googleapis.com: ${await probe(() => fetch("https://securetoken.googleapis.com/", { mode: "no-cors", cache: "no-store" }))}`,
    `auth domain: ${await probe(() => fetch("https://beawareconfcapportal.firebaseapp.com/__/firebase/init.json", { mode: "no-cors", cache: "no-store" }))}`, // no-cors: only reachability matters, and a cross-origin read is blocked on purpose
    `serviceWorkers: ${await probe(async () => (await navigator.serviceWorker.getRegistrations()).length)}`,
  ];
  console.warn("[portal diagnostics]\n" + lines.join("\n"));
  return lines;
};
